/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useState } from 'react'

import { FiMonitor } from 'react-icons/fi'
import { TbPolygon } from 'react-icons/tb'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'

import { Alert, Loader } from '@/components/ui'
import { useAuth, useCompany } from '@/hooks'
import useMediaQuery from '@/hooks/useMediaQuery'
import { firstAppPath } from '@/utils/appHome'

import { GeofenceEditor } from './GeofenceEditor'

import { useGeofence, useGeofenceCategories, useGeofenceCompany, useManagedGeofences } from '../hooks'
import type { GeofenceEditorNavigationState } from '../types'

const Message = ({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) => (
  <div className="flex h-full min-h-0 flex-col p-4">
    <div className="flex min-h-10 shrink-0 items-center gap-2 border-b border-base-200 bg-base-100 px-4 py-2">
      <TbPolygon className="text-lg text-primary" />
      <span className="text-lg font-semibold">Geofences</span>
    </div>
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
      <span className="text-4xl text-base-content/30">{icon}</span>
      <p className="text-lg font-medium">{title}</p>
      <p className="max-w-sm text-sm text-base-content/60">{text}</p>
      <Link to="/geofences" className="btn btn-sm btn-primary">
        Back to geofences
      </Link>
    </div>
  </div>
)

/**
 * New / Edit geofence (`/geofences/new`, `/geofences/:geofenceId/edit`). Loads what the editor
 * needs — the geofence, the company's other geofences and its categories — then hands over.
 * Gated on the `geofences` grant like the list, and desktop only: drawing on a phone isn't
 * something Refresh offered either.
 */
export const GeofenceEditorPage = () => {
  const { geofenceId } = useParams<{ geofenceId: string }>()
  const { hasPermission } = useAuth()
  const { companies } = useCompany()
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const location = useLocation()
  const navigationState = location.state as GeofenceEditorNavigationState | null
  const [selectedCompanyId] = useGeofenceCompany()
  const [reloads, setReloads] = useState(0)

  const id = geofenceId ? Number(geofenceId) : undefined
  const detail = useGeofence(id, !navigationState?.geofence)
  // Editing: the geofence's own company, whatever the list had picked.
  const geofence = reloads > 0 ? detail.data : (navigationState?.geofence ?? detail.data)
  const companyId = geofence?.companyId ?? selectedCompanyId
  const geofences = useManagedGeofences(companyId)
  const categories = useGeofenceCategories(companyId)

  if (!hasPermission('geofences')) {
    return <Navigate to={firstAppPath(hasPermission)} replace />
  }
  if (!isDesktop) {
    return (
      <Message
        icon={<FiMonitor />}
        title="Editing needs a larger screen"
        text="Drawing geofences needs a desktop or laptop. You can still view them on this device."
      />
    )
  }
  if (id !== undefined && (!Number.isInteger(id) || detail.isError)) {
    return (
      <Message
        icon={<TbPolygon />}
        title="Geofence not found"
        text="It may have been deleted, or it belongs to a company you can't see."
      />
    )
  }
  if (!companyId || detail.isLoading || geofences.isLoading || categories.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    )
  }
  if (geofences.isError || categories.isError) {
    return (
      <div className="p-4">
        <Alert type="error" message="Couldn’t load the company’s geofences" description="Please try again." />
      </div>
    )
  }

  const company = companies.find((c) => c.id === companyId)
  return (
    <GeofenceEditor
      // A reload starts the editor over from what the server has now.
      key={`${id ?? 'new'}-${companyId}-${reloads}-${geofence?.version ?? 0}`}
      companyId={companyId}
      companyName={company?.fullName ?? company?.name ?? ''}
      geofence={geofence}
      geofences={geofences.data ?? []}
      categories={categories.data ?? []}
      onReload={() => {
        void detail.refetch().then(() => setReloads((n) => n + 1))
      }}
    />
  )
}

export default GeofenceEditorPage
